import axios, { type AxiosError } from 'axios';

import { API_CONFIG, API_ERROR_CODES, AUTH_CONFIG, ENV, parsePrice } from '@/config';
import { apiCache, buildCacheKey, getInvalidationPatterns, isCacheableMethod } from '@/utils/cache';
import { storage } from '@/utils/storage';

import type {
  AddReviewRequest,
  AddToCartRequest,
  ApiResponse,
  AuthResponseData,
  CartApi,
  CartItemApi,
  CategoryApi,
  CreateOrderRequest,
  ICartApi,
  IOrdersApi,
  IProductsApi,
  IWishlistApi,
  LoginRequest,
  OrderApi,
  ProductApi,
  ProductFilterRequest,
  RefreshTokenResponse,
  RegisterRequest,
  ReviewApi,
  UpdateCartItemRequest,
  UpdateProfileRequest,
  UserApi,
  WishlistApi,
} from './types';

const shouldUseMock = (): boolean => ENV.VITE_MODE === 'development' || ENV.VITE_MODE === 'test';

const getMockProducts = async () => {
  if (!shouldUseMock()) throw new Error('Mocks not available in production');
  const m = await import('./mock');
  return m.products as unknown as ProductApi[];
};

const getMockCategories = async () => {
  if (!shouldUseMock()) throw new Error('Mocks not available in production');
  const m = await import('./mock');
  return m.categories;
};

const getMockSizes = async () => {
  if (!shouldUseMock()) throw new Error('Mocks not available in production');
  const m = await import('./mock');
  return m.sizes;
};

const getMockColors = async () => {
  if (!shouldUseMock()) throw new Error('Mocks not available in production');
  const m = await import('./mock');
  return m.colors;
};

const api = axios.create({
  baseURL: API_CONFIG.baseUrl,
  timeout: API_CONFIG.timeout,
  headers: {
    ...API_CONFIG.headers,
  },
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(p => {
    if (error) {
      p.reject(error);
    } else if (token) {
      p.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.request.use(
  config => {
    const token = storage.get<string>(AUTH_CONFIG.tokenKey);
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

api.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;
    if (!originalRequest) return Promise.reject(error);

    if (error.response?.status !== 401 || (originalRequest as any)?._retry) {
      return Promise.reject(error);
    }

    if (originalRequest?.url?.includes('/auth/refresh')) {
      storage.remove(AUTH_CONFIG.tokenKey);
      storage.remove(AUTH_CONFIG.refreshTokenKey);
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<unknown>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then(token => {
          if (originalRequest?.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return api(originalRequest);
        })
        .catch(err => Promise.reject(err));
    }

    (originalRequest as any)._retry = true;
    isRefreshing = true;

    const refreshToken = storage.get<string>(AUTH_CONFIG.refreshTokenKey);
    if (!refreshToken) {
      storage.remove(AUTH_CONFIG.tokenKey);
      processQueue(new Error('No refresh token available'));
      isRefreshing = false;
      return Promise.reject(error);
    }

    try {
      const response = await api.post<{
        success: boolean;
        data?: { token: string; refreshToken: string };
      }>('/auth/refresh', { refreshToken });

      const data = response.data;
      if (data.success && data.data) {
        const newToken = data.data.token;
        const newRefreshToken = data.data.refreshToken;

        storage.set(AUTH_CONFIG.tokenKey, newToken);
        storage.set(AUTH_CONFIG.refreshTokenKey, newRefreshToken);

        if (originalRequest?.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }

        processQueue(null, newToken);
        return api(originalRequest);
      }

      storage.remove(AUTH_CONFIG.tokenKey);
      storage.remove(AUTH_CONFIG.refreshTokenKey);
      processQueue(new Error('Refresh failed'));
      return Promise.reject(error);
    } catch {
      storage.remove(AUTH_CONFIG.tokenKey);
      storage.remove(AUTH_CONFIG.refreshTokenKey);
      processQueue(new Error('Refresh failed'));
      return Promise.reject(error);
    } finally {
      isRefreshing = false;
    }
  }
);

const fetchApi = async <T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> => {
  const method = (options.method || 'GET').toUpperCase();

  let requestBody: unknown = undefined;
  if (options.body) {
    try {
      requestBody = JSON.parse(options.body as string);
    } catch {
      requestBody = options.body;
    }
  }
  const cacheKey = buildCacheKey(method, endpoint, requestBody);

  if (isCacheableMethod(method)) {
    const cached = apiCache.get<ApiResponse<T>>(cacheKey);
    if (cached) {
      return cached;
    }
  }

  if (!isCacheableMethod(method)) {
    const patterns = getInvalidationPatterns(method, endpoint);
    patterns.forEach(p => apiCache.invalidatePattern(p));
  }

  try {
    let data: unknown = undefined;
    if (options.body) {
      try {
        data = JSON.parse(options.body as string);
      } catch {
        data = options.body;
      }
    }

    const customHeaders: Record<string, string> = {};
    if (options.headers) {
      new Headers(options.headers).forEach((v, k) => {
        customHeaders[k] = v;
      });
    }

    const response = await api.request<unknown>({
      url: endpoint,
      method,
      data,
      headers: customHeaders,
    });

    const result = response.data;
    const apiResult =
      result && typeof result === 'object' && 'success' in result
        ? (result as ApiResponse<T>)
        : { success: true, data: result as T };

    if (isCacheableMethod(method) && apiResult.success && !endpoint.startsWith('/auth/')) {
      apiCache.set(cacheKey, apiResult, 60_000);
    }

    return apiResult;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const errorData = error.response?.data;
      if (status) {
        return {
          success: false,
          error: {
            code: `HTTP_${status}`,
            message: errorData?.message || `HTTP error ${status}`,
          },
        };
      }
    }
    return {
      success: false,
      error: {
        code: API_ERROR_CODES.NETWORK_ERROR,
        message: error instanceof Error ? error.message : 'Network error',
      },
    };
  }
};

export const authApi = {
  login: (data: LoginRequest) =>
    fetchApi<AuthResponseData>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  register: (data: RegisterRequest) =>
    fetchApi<AuthResponseData>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  logout: () => fetchApi<void>('/auth/logout', { method: 'POST' }),

  forgotPassword: (email: string) =>
    fetchApi<void>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  resetPassword: (token: string, newPassword: string) =>
    fetchApi<void>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password: newPassword }),
    }),

  getCurrentUser: () => fetchApi<UserApi>('/auth/me'),

  updateProfile: (data: UpdateProfileRequest) =>
    fetchApi<UserApi>('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  changePassword: (currentPassword: string, newPassword: string) =>
    fetchApi<void>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),

  refreshToken: (refreshToken?: string) =>
    fetchApi<RefreshTokenResponse>('/auth/refresh', {
      method: 'POST',
      body: refreshToken
        ? JSON.stringify({ refreshToken })
        : JSON.stringify({ refreshToken: storage.get<string>(AUTH_CONFIG.refreshTokenKey) }),
    }),
};

export const productsApi: IProductsApi = {
  getProducts: async (filters?: ProductFilterRequest) => {
    const mockProducts = await getMockProducts();
    let list = [...mockProducts];

    if (filters) {
      if (filters.category) {
        list = list.filter(p => p.category === filters.category);
      }
      if (filters.isSale !== undefined) {
        list = list.filter(
          p =>
            p.isSale === filters.isSale ||
            (filters.isSale && p.originalPrice && parsePrice(p.originalPrice) > parsePrice(p.price))
        );
      }
      if (filters.sizes) {
        const targetSizes = Array.isArray(filters.sizes) ? filters.sizes : [filters.sizes];
        list = list.filter(p => p.sizes?.some(s => targetSizes.includes(s)));
      }
      if (filters.colors) {
        const targetColors = Array.isArray(filters.colors) ? filters.colors : [filters.colors];
        list = list.filter(p => {
          const pColor = p.colors || '';
          if (Array.isArray(pColor)) {
            return pColor.some(c => targetColors.includes(c));
          }
          return targetColors.includes(pColor);
        });
      }
      if (filters.minPrice !== undefined) {
        const minPrice = filters.minPrice;
        list = list.filter(p => parsePrice(p.price) >= minPrice);
      }
      if (filters.maxPrice !== undefined) {
        const maxPrice = filters.maxPrice;
        list = list.filter(p => parsePrice(p.price) <= maxPrice);
      }
      if (filters.search) {
        const q = filters.search.toLowerCase();
        list = list.filter(
          p => p.name.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q)
        );
      }
      if (filters.sortBy) {
        if (filters.sortBy === 'newest') {
          list = list.sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
        } else if (filters.sortBy === 'price-low') {
          list = list.sort((a, b) => parsePrice(a.price) - parsePrice(b.price));
        } else if (filters.sortBy === 'price-high') {
          list = list.sort((a, b) => parsePrice(b.price) - parsePrice(a.price));
        } else if (filters.sortBy === 'rating') {
          list = list.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        } else if (filters.sortBy === 'popular' || filters.sortBy === 'featured') {
          list = list.sort((a, b) => (b.reviewCount || 0) - (a.reviewCount || 0));
        }
      }
      if (filters.limit) {
        list = list.slice(0, filters.limit);
      }
    }

    return {
      success: true,
      data: list,
    };
  },

  getProduct: async (id: string) => {
    const mockProducts = await getMockProducts();
    const product = mockProducts.find(p => p.id === id);
    if (!product) {
      return {
        success: false,
        error: {
          code: 'NOT_FOUND',
          message: 'Product not found',
        },
      };
    }
    return {
      success: true,
      data: product,
    };
  },

  getCategories: async () => {
    const mockCategories = await getMockCategories();
    const mappedCategories: CategoryApi[] = mockCategories.map(cat => ({
      id: cat.name,
      name: cat.name,
      slug: cat.name.toLowerCase().replace(/ /g, '-'),
      label: cat.label,
      productCount: cat.itemCount,
      itemCount: cat.itemCount,
    }));
    return {
      success: true,
      data: mappedCategories,
    };
  },

  getSizes: async () => ({
    success: true,
    data: await getMockSizes(),
  }),

  getColors: async () => ({
    success: true,
    data: await getMockColors(),
  }),

  getCategory: async (slug: string) => {
    const mockCategories = await getMockCategories();
    const cat = mockCategories.find(c => c.name.toLowerCase().replace(/ /g, '-') === slug);
    if (!cat) {
      return {
        success: false,
        error: {
          code: 'NOT_FOUND',
          message: 'Category not found',
        },
      };
    }
    const categoryApi: CategoryApi = {
      id: cat.name,
      name: cat.name,
      slug,
      label: cat.label,
      productCount: cat.itemCount,
      itemCount: cat.itemCount,
    };
    return {
      success: true,
      data: categoryApi,
    };
  },

  getRelatedProducts: async (productId: string) => {
    const mockProducts = await getMockProducts();
    const product = mockProducts.find(p => p.id === productId);
    let list = mockProducts.filter(p => p.id !== productId);
    if (product) {
      list = list.filter(p => p.category === product.category);
    }
    return {
      success: true,
      data: list.slice(0, 4),
    };
  },

  getProductReviews: async (_productId: string) => ({
    success: true,
    data: [],
  }),

  addReview: async (data: AddReviewRequest) => ({
    success: true,
    data: {
      id: Math.random().toString(),
      productId: data.productId,
      rating: data.rating,
      comment: data.comment,
      userName: data.userName,
      createdAt: new Date().toISOString(),
    } as unknown as ReviewApi,
  }),

  searchProducts: async (query: string, limit = 10) => {
    const mockProducts = await getMockProducts();
    const q = query.toLowerCase();
    const list = mockProducts.filter(
      p => p.name.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q)
    );
    return {
      success: true,
      data: list.slice(0, limit),
    };
  },
};

export const cartApi: ICartApi = {
  getCart: () => fetchApi<CartApi>('/cart'),

  addToCart: (data: AddToCartRequest) =>
    fetchApi<CartItemApi>('/cart/items', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateCartItem: (data: UpdateCartItemRequest) =>
    fetchApi<CartItemApi>(`/cart/items/${data.itemId}`, {
      method: 'PUT',
      body: JSON.stringify({ quantity: data.quantity }),
    }),

  removeFromCart: (itemId: string) => fetchApi<void>(`/cart/items/${itemId}`, { method: 'DELETE' }),

  clearCart: () => fetchApi<void>('/cart', { method: 'DELETE' }),

  mergeGuestCart: (guestCartId: string) =>
    fetchApi<CartApi>('/cart/merge', {
      method: 'POST',
      body: JSON.stringify({ guestCartId }),
    }),
};

export const ordersApi: IOrdersApi = {
  createOrder: (data: CreateOrderRequest) =>
    fetchApi<OrderApi>('/orders', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getOrders: () => fetchApi<OrderApi[]>('/orders'),

  getOrder: (id: string) => fetchApi<OrderApi>(`/orders/${id}`),

  cancelOrder: (id: string, reason?: string) =>
    fetchApi<OrderApi>(`/orders/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  trackOrder: (id: string) =>
    fetchApi<{ status: string; trackingNumber: string; events: unknown[] }>(`/orders/${id}/track`),

  reorder: (orderId: string) => fetchApi<CartApi>(`/orders/${orderId}/reorder`, { method: 'POST' }),
};

export const wishlistApi: IWishlistApi = {
  getWishlist: () => fetchApi<WishlistApi>('/wishlist'),

  addToWishlist: (productId: string) =>
    fetchApi<WishlistApi>('/wishlist', {
      method: 'POST',
      body: JSON.stringify({ productId }),
    }),

  removeFromWishlist: (productId: string) =>
    fetchApi<WishlistApi>(`/wishlist/${productId}`, { method: 'DELETE' }),

  moveToCart: (productId: string) =>
    fetchApi<CartApi>(`/wishlist/${productId}/move-to-cart`, { method: 'POST' }),

  isInWishlist: (productId: string) => fetchApi<boolean>(`/wishlist/${productId}/check`),
};
